import { useEffect, useMemo, useState } from "react";
import { pdfjs } from "react-pdf";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { supabase } from "@/lib/supabase";
import { ImageWithRegions as SharedImageWithRegions } from "@/components/ImageWithRegions";
import { getQuestionRubricAccentColor, HIGHLIGHT_STROKE } from "@/lib/regionColors";
import type {
  BoundingBox,
  RegionType,
  QuestionRegion,
  QuestionIdentifierResult,
  ExtractedQuestion,
  RubricCriterion,
  QuestionRubric,
  GraderResult,
} from "@/types/pipeline";
import { extractQuestionsFromRegions } from "@/types/pipeline";
import { processQuestionIdentifier } from "@/services/questionIdentifierService";
import { suggestRubricsBatch, suggestSingleRubric } from "@/services/rubricService";

type TaskKey =
  | "questionIdentifier"
  | "rubricEditor"
  | "grader"
  | "submissionGrader"
  | "submissionGraderOcr";

type TaskConfig = {
  provider: string;
  model: string;
  notes: string;
};

type BenchmarkImage = {
  name: string;
  dataUrl: string; // first page (kept for backward-compat)
  pages: string[]; // one entry per page; length 1 for images, N for PDFs
  updatedAt: string;
};

const MAX_IMAGE_DIMENSION = 2048;

async function pdfFileToImages(file: File): Promise<string[]> {
  const dataUrl = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(file);
  });
  const pdf = await pdfjs.getDocument(dataUrl).promise;
  const images: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1 });
    const scale = Math.min(
      MAX_IMAGE_DIMENSION / viewport.width,
      MAX_IMAGE_DIMENSION / viewport.height,
      2,
    );
    const scaledViewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = scaledViewport.width;
    canvas.height = scaledViewport.height;
    const ctx = canvas.getContext("2d")!;
    await page.render({
      canvasContext: ctx,
      viewport: scaledViewport,
      canvas,
    } as Parameters<typeof page.render>[0]).promise;
    images.push(canvas.toDataURL("image/jpeg", 0.85));
  }
  return images;
}

const STORAGE_KEY = "dev-model-eval-config";
const BENCHMARK_KEY = "dev-model-eval-benchmark";
const RESULTS_KEY = "dev-model-eval-results";
const STUDENT_SUBMISSION_KEY = "dev-model-eval-student-submission";
const SUBMISSION_RESULTS_KEY = "dev-model-eval-submission-results";

const TASKS: Array<{
  key: TaskKey;
  title: string;
  description: string;
}> = [
  {
    key: "questionIdentifier",
    title: "Task 1: Question Identifier + OCR",
    description: "偵測題目區域、邊界框，並辨識手寫作答內容",
  },
  {
    key: "rubricEditor",
    title: "Task 2: Rubric Editor",
    description: "定義或由 AI 建議各題評分標準，教師可逐題編輯",
  },
  {
    key: "grader",
    title: "Task 3: Question Grader",
    description: "依評分標準逐題評分與回饋",
  },
  {
    key: "submissionGrader",
    title: "Task 4: Submission Grader (Visual)",
    description: "上傳學生作答，AI 偵測答案區域、批改並以視覺呈現結果",
  },
];

const PROVIDERS = [
  "openai",
  "bytedance",
  "alibaba",
  "deepseek",
  "iflytek",
  "zai",
];

const MODEL_SUGGESTIONS: Record<string, string[]> = {
  openai: ["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-5.2"],
  bytedance: [
    "seed-2-0-pro-260328",
    "seed-2-0-lite-260228",
    "seed-2-0-mini-260215",
    "seed-1-8-251228",
    "seed-1-6-250915",
    "seed-1-6-flash-250715",
    "skylark-vision-250515",
  ],
  alibaba: [
    // Qwen-VL (Vision-Language) models - for image understanding
    "qwen-vl-max", // Most capable, best for complex tasks
    "qwen-vl-max-latest", // Latest version of max
    "qwen-vl-plus", // Balanced performance/cost
    "qwen-vl-plus-latest", // Latest version of plus
    // Qwen2.5-VL models - newer generation
    "qwen2.5-vl-72b-instruct", // 72B parameter model
    "qwen2.5-vl-7b-instruct", // 7B parameter model
    // Qwen-VL-OCR - specialized for OCR tasks
    "qwen-vl-ocr", // Optimized for text extraction
    "qwen-vl-ocr-latest", // Latest OCR model
  ],
  deepseek: ["deepseek-chat"],
  iflytek: ["spark-3.5", "spark-4.0"],
  zai: [
    "glm-5.1",
    "glm-5-turbo",
    "glm-5",
    "glm-4.7-flash",
    "glm-4.7",
    "glm-4.6",
    "glm-4.5-air",
    "glm-4.5",
  ],
};

const DEFAULT_CONFIG: Record<TaskKey, TaskConfig> = {
  questionIdentifier: { provider: "openai", model: "gpt-4o", notes: "" },
  rubricEditor: { provider: "openai", model: "gpt-4o", notes: "" },
  grader: { provider: "openai", model: "gpt-4o", notes: "" },
  submissionGrader: { provider: "openai", model: "gpt-4o", notes: "" },
  submissionGraderOcr: { provider: "openai", model: "gpt-4o", notes: "" },
};

// Color palette for differentiating bounding boxes
const BBOX_COLORS = {
  // Section instruction colors (darker, more prominent)
  sectionInstruction: [
    "#c62828", // dark red
    "#6a1b9a", // dark purple
    "#00695c", // dark teal
    "#e65100", // dark orange
  ],
  // Section content colors (muted, distinct from instructions)
  sectionContent: [
    "#5d4037", // brown
    "#37474f", // blue-grey
    "#4e342e", // dark brown
    "#455a64", // grey-blue
  ],
  // Question colors (lighter, varied)
  question: [
    "#1e88e5", // blue
    "#43a047", // green
    "#fb8c00", // orange
    "#8e24aa", // purple
    "#00acc1", // cyan
    "#ffb300", // amber
    "#6d4c41", // brown
    "#d81b60", // pink
    "#3949ab", // indigo
    "#e53935", // red
  ],
  // Answer element colors (brighter, distinct)
  answerText: "#2e7d32", // green - handwritten text
  answerMc: "#1565c0", // blue - MC selection
  answerSymbol: "#f57c00", // orange - symbols/marks
  answerDrawing: "#7b1fa2", // purple - drawings
};

function PageNavigator({
  pages,
  page,
  onPage,
}: {
  pages: string[];
  page: number;
  onPage: (n: number) => void;
}) {
  if (pages.length <= 1) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
      <Button
        size="small"
        variant="outlined"
        onClick={() => onPage(Math.max(0, page - 1))}
        disabled={page === 0}
        sx={{ minWidth: 32, px: 0.5 }}
      >
        ‹
      </Button>
      <Typography variant="caption" sx={{ minWidth: 60, textAlign: "center" }}>
        第 {page + 1} / {pages.length} 頁
      </Typography>
      <Button
        size="small"
        variant="outlined"
        onClick={() => onPage(Math.min(pages.length - 1, page + 1))}
        disabled={page === pages.length - 1}
        sx={{ minWidth: 32, px: 0.5 }}
      >
        ›
      </Button>
    </Stack>
  );
}

function ImageWithRegions({
  src,
  regions,
  highlightIndex,
  showCoordinates = true,
}: {
  src: string;
  regions: QuestionRegion[];
  highlightIndex?: number;
  showCoordinates?: boolean;
}) {
  // Track color indices separately for each region type
  const colorIndices = { instruction: 0, content: 0, question: 0 };

  const getColorForRegion = (region: QuestionRegion, index: number): string => {
    if (index === highlightIndex) return "#f44336"; // Red highlight matching HIGHLIGHT_STROKE

    switch (region.type) {
      case "section-instruction": {
        const idx =
          colorIndices.instruction % BBOX_COLORS.sectionInstruction.length;
        colorIndices.instruction++;
        return BBOX_COLORS.sectionInstruction[idx] ?? "#c62828";
      }
      case "section-content": {
        const idx = colorIndices.content % BBOX_COLORS.sectionContent.length;
        colorIndices.content++;
        return BBOX_COLORS.sectionContent[idx] ?? "#5d4037";
      }
      case "answer-text":
        return BBOX_COLORS.answerText;
      case "answer-mc":
        return BBOX_COLORS.answerMc;
      case "answer-symbol":
        return BBOX_COLORS.answerSymbol;
      case "answer-drawing":
        return BBOX_COLORS.answerDrawing;
      default: {
        // question, mc-question, or unknown
        const idx = colorIndices.question % BBOX_COLORS.question.length;
        colorIndices.question++;
        return BBOX_COLORS.question[idx] ?? "#1e88e5";
      }
    }
  };

  // Helper to determine if this is an answer element (smaller, different styling)
  const isAnswerElement = (region: QuestionRegion): boolean => {
    return region.type?.startsWith("answer-") ?? false;
  };

  // Reset counters for each render
  colorIndices.instruction = 0;
  colorIndices.content = 0;
  colorIndices.question = 0;
  const regionColors = regions.map((region, index) =>
    getColorForRegion(region, index),
  );

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Box
        component="img"
        src={src}
        alt="Benchmark"
        sx={{
          width: "100%",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      />
      <Box
        component="svg"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {/* Page coordinate labels and margin rulers */}
        {showCoordinates && (
          <>
            {/* Corner labels */}
            <text
              x={0.01}
              y={0.025}
              fill="#666"
              fontSize={0.018}
              fontFamily="monospace"
            >
              (0, 0)
            </text>
            <text
              x={0.99}
              y={0.025}
              fill="#666"
              fontSize={0.018}
              fontFamily="monospace"
              textAnchor="end"
            >
              (1, 0)
            </text>
            <text
              x={0.01}
              y={0.99}
              fill="#666"
              fontSize={0.018}
              fontFamily="monospace"
            >
              (0, 1)
            </text>
            <text
              x={0.99}
              y={0.99}
              fill="#666"
              fontSize={0.018}
              fontFamily="monospace"
              textAnchor="end"
            >
              (1, 1)
            </text>

            {/* Top margin ruler (tick marks at 0.1 intervals) */}
            {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map((tick) => (
              <g key={`top-${tick}`}>
                <line
                  x1={tick}
                  y1={0}
                  x2={tick}
                  y2={0.015}
                  stroke="#999"
                  strokeWidth={0.002}
                />
                <text
                  x={tick}
                  y={0.028}
                  fill="#999"
                  fontSize={0.012}
                  fontFamily="monospace"
                  textAnchor="middle"
                >
                  {tick.toFixed(1)}
                </text>
              </g>
            ))}

            {/* Left margin ruler (tick marks at 0.1 intervals) */}
            {[0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9].map((tick) => (
              <g key={`left-${tick}`}>
                <line
                  x1={0}
                  y1={tick}
                  x2={0.012}
                  y2={tick}
                  stroke="#999"
                  strokeWidth={0.002}
                />
                <text
                  x={0.016}
                  y={tick + 0.005}
                  fill="#999"
                  fontSize={0.012}
                  fontFamily="monospace"
                >
                  {tick.toFixed(1)}
                </text>
              </g>
            ))}
          </>
        )}

        {regions.map((region, index) => {
          const { x, y, width, height } = region.bbox;
          const color = regionColors[index];
          const isHighlighted = index === highlightIndex;
          const x2 = x + width;
          const y2 = y + height;
          const isSectionInstruction = region.type === "section-instruction";
          const isSectionContent = region.type === "section-content";
          const isAnswer = isAnswerElement(region);

          // Calculate label width based on type
          let labelWidth = 0.08;
          if (isSectionInstruction) labelWidth = 0.12;
          else if (isSectionContent) labelWidth = 0.14;
          else if (isAnswer) labelWidth = 0.16; // Longer for answer info

          // Determine fill and stroke styles based on type
          let fillColor = "rgba(0,0,0,0)";
          let strokeWidth = 0.003;
          let strokeDasharray: string | undefined = undefined;
          let labelIcon = "";
          let labelText = region.questionId;

          if (isHighlighted) {
            fillColor = "rgba(244, 67, 54, 0.15)"; // Red highlight fill
          } else if (isSectionInstruction) {
            fillColor = "rgba(198, 40, 40, 0.08)";
            strokeWidth = 0.004;
            strokeDasharray = "0.01 0.005";
            labelIcon = "📋 ";
          } else if (isSectionContent) {
            fillColor = "rgba(93, 64, 55, 0.06)";
            strokeWidth = 0.003;
            strokeDasharray = "0.006 0.004";
            labelIcon = "📖 ";
          } else if (region.type === "answer-text") {
            fillColor = "rgba(46, 125, 50, 0.12)";
            strokeWidth = 0.002;
            labelIcon = "✏️ ";
            // Show transcription preview if available
            if (region.transcription) {
              labelText =
                region.transcription.slice(0, 8) +
                (region.transcription.length > 8 ? "…" : "");
            }
          } else if (region.type === "answer-mc") {
            fillColor = "rgba(21, 101, 192, 0.15)";
            strokeWidth = 0.002;
            labelIcon = "⭕ ";
            // Show selected option
            if (region.selectedOption) {
              labelText = `選: ${region.selectedOption}`;
            }
          } else if (region.type === "answer-symbol") {
            fillColor = "rgba(245, 124, 0, 0.12)";
            strokeWidth = 0.002;
            labelIcon = "✓ ";
            if (region.symbolType) {
              labelText = region.symbolType;
            }
          } else if (region.type === "answer-drawing") {
            fillColor = "rgba(123, 31, 162, 0.10)";
            strokeWidth = 0.002;
            strokeDasharray = "0.004 0.002";
            labelIcon = "🎨 ";
            labelText = "圖";
          }

          return (
            <g key={`${region.questionId}-${index}`}>
              {/* Bounding box rectangle */}
              <rect
                x={x}
                y={y}
                width={width}
                height={height}
                fill={fillColor}
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray={strokeDasharray}
              />

              {/* Region ID label */}
              <rect
                x={x}
                y={y - (isAnswer ? 0.018 : 0.024)}
                width={Math.min(labelWidth, width)}
                height={isAnswer ? 0.018 : 0.024}
                fill={color}
                rx={isAnswer ? 0.003 : 0}
              />
              <text
                x={x + 0.004}
                y={y - (isAnswer ? 0.004 : 0.007)}
                fill="#fff"
                fontSize={
                  isAnswer
                    ? 0.01
                    : isSectionInstruction || isSectionContent
                      ? 0.012
                      : 0.014
                }
                fontFamily="sans-serif"
                fontWeight="bold"
              >
                {labelIcon}
                {labelText}
              </text>

              {/* Show coordinates for non-answer elements only (to reduce clutter) */}
              {showCoordinates && !isAnswer && (
                <>
                  {/* Upper-left coordinate */}
                  <text
                    x={x + 0.003}
                    y={y + 0.018}
                    fill={color}
                    fontSize={0.012}
                    fontFamily="monospace"
                    fontWeight="bold"
                  >
                    ({x.toFixed(2)}, {y.toFixed(2)})
                  </text>

                  {/* Lower-right coordinate */}
                  <text
                    x={x2 - 0.003}
                    y={y2 - 0.006}
                    fill={color}
                    fontSize={0.012}
                    fontFamily="monospace"
                    fontWeight="bold"
                    textAnchor="end"
                  >
                    ({x2.toFixed(2)}, {y2.toFixed(2)})
                  </text>
                </>
              )}
            </g>
          );
        })}
      </Box>
    </Box>
  );
}

export function DevModelEvalPage() {
  const [configs, setConfigs] =
    useState<Record<TaskKey, TaskConfig>>(DEFAULT_CONFIG);
  const [activeTask, setActiveTask] = useState<TaskKey>("questionIdentifier");
  const [benchmark, setBenchmark] = useState<BenchmarkImage | null>(null);
  const [benchmarkPage, setBenchmarkPage] = useState(0);
  const [studentSubmission, setStudentSubmission] =
    useState<BenchmarkImage | null>(null);
  const [studentPage, setStudentPage] = useState(0);
  const [regionIndex, setRegionIndex] = useState(0);
  const [gradeLevel, setGradeLevel] = useState("中四");
  const [isRunning, setIsRunning] = useState(false);
  const [runningStep, setRunningStep] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [results, setResults] = useState<{
    questionIdentifier?: QuestionIdentifierResult;
    extractedQuestions?: ExtractedQuestion[];
    rubrics?: QuestionRubric[];
    grader?: GraderResult;
  }>({});
  const [submissionResults, setSubmissionResults] = useState<{
    questionIdentifier?: QuestionIdentifierResult;
    extractedQuestions?: ExtractedQuestion[];
    grader?: GraderResult;
  } | null>(null);
  const [hoveredQuestionId, setHoveredQuestionId] = useState<string | null>(
    null,
  );
  const [suggestingQuestion, setSuggestingQuestion] = useState<string | null>(null);
  const [rubricPageIndex, setRubricPageIndex] = useState(0);
  const [focusedQuestionId, setFocusedQuestionId] = useState<string | null>(null);
  const [serverSavedAt, setServerSavedAt] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  // Load config from server first, then fall back to localStorage
  useEffect(() => {
    const loadConfig = async () => {
      try {
        // Try to load from server first
        const response = await fetch("/api/dev/model-eval/config");
        if (response.ok) {
          const data = await response.json();
          if (data.exists && data.configs) {
            setConfigs((prev) => ({ ...prev, ...data.configs }));
            setServerSavedAt(data.savedAt);
            return; // Server config loaded, skip localStorage
          }
        }
      } catch (error) {
        console.warn(
          "Failed to load server config, falling back to localStorage:",
          error,
        );
      }

      // Fall back to localStorage
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;
      try {
        const parsed = JSON.parse(stored) as Record<TaskKey, TaskConfig>;
        setConfigs((prev) => ({ ...prev, ...parsed }));
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    };

    loadConfig();
  }, []);

  useEffect(() => {
    const storedBenchmark = localStorage.getItem(BENCHMARK_KEY);
    if (storedBenchmark) {
      try {
        const parsed = JSON.parse(storedBenchmark) as BenchmarkImage;
        // Normalize legacy data that doesn't have `pages`
        if (!parsed.pages) parsed.pages = [parsed.dataUrl];
        setBenchmark(parsed);
      } catch {
        localStorage.removeItem(BENCHMARK_KEY);
      }
    }

    const storedStudentSubmission = localStorage.getItem(
      STUDENT_SUBMISSION_KEY,
    );
    if (storedStudentSubmission) {
      try {
        const parsed = JSON.parse(storedStudentSubmission) as BenchmarkImage;
        if (!parsed.pages) parsed.pages = [parsed.dataUrl];
        setStudentSubmission(parsed);
      } catch {
        localStorage.removeItem(STUDENT_SUBMISSION_KEY);
      }
    }

    const storedResults = localStorage.getItem(RESULTS_KEY);
    if (storedResults) {
      try {
        setResults(JSON.parse(storedResults));
      } catch {
        localStorage.removeItem(RESULTS_KEY);
      }
    }

    const storedSubmissionResults = localStorage.getItem(
      SUBMISSION_RESULTS_KEY,
    );
    if (storedSubmissionResults) {
      try {
        setSubmissionResults(JSON.parse(storedSubmissionResults));
      } catch {
        localStorage.removeItem(SUBMISSION_RESULTS_KEY);
      }
    }
  }, []);

  const suggestionsByTask = useMemo(() => {
    return TASKS.reduce<Record<TaskKey, string[]>>(
      (acc, task) => {
        const provider = configs[task.key]?.provider;
        acc[task.key] = provider ? MODEL_SUGGESTIONS[provider] || [] : [];
        return acc;
      },
      {} as Record<TaskKey, string[]>,
    );
  }, [configs]);

  const handleChange = (taskKey: TaskKey, next: Partial<TaskConfig>) => {
    setConfigs((prev) => ({
      ...prev,
      [taskKey]: { ...prev[taskKey], ...next },
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Save to server
      const response = await fetch("/api/dev/model-eval/config", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ configs }),
      });

      if (response.ok) {
        const data = await response.json();
        setServerSavedAt(data.savedAt);
        setSaveMessage({
          type: "success",
          text: `已儲存至伺服器 (${new Date(data.savedAt).toLocaleString()})`,
        });
        // Also save to localStorage as backup
        localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
      } else {
        throw new Error("Server save failed");
      }
    } catch (error) {
      // Fall back to localStorage only
      localStorage.setItem(STORAGE_KEY, JSON.stringify(configs));
      setSaveMessage({
        type: "error",
        text: "伺服器儲存失敗，已儲存至本機瀏覽器",
      });
    } finally {
      setIsSaving(false);
      // Clear message after 5 seconds
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  const handleReset = () => {
    setConfigs(DEFAULT_CONFIG);
    setServerSavedAt(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const persistResults = (nextResults: typeof results) => {
    setResults(nextResults);
    localStorage.setItem(RESULTS_KEY, JSON.stringify(nextResults));
  };

  const handleUpload = async (file: File) => {
    let pages: string[];
    if (
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf")
    ) {
      pages = await pdfFileToImages(file);
    } else {
      pages = await new Promise<string[]>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve([reader.result as string]);
        reader.readAsDataURL(file);
      });
    }
    const nextBenchmark: BenchmarkImage = {
      name: file.name,
      dataUrl: pages[0] ?? "",
      pages,
      updatedAt: new Date().toISOString(),
    };
    setBenchmark(nextBenchmark);
    setBenchmarkPage(0);
    localStorage.setItem(BENCHMARK_KEY, JSON.stringify(nextBenchmark));
  };

  const handleClearBenchmark = () => {
    setBenchmark(null);
    setBenchmarkPage(0);
    localStorage.removeItem(BENCHMARK_KEY);
    persistResults({});
    setRegionIndex(0);
  };

  const handleUploadStudentSubmission = async (file: File) => {
    let pages: string[];
    if (
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf")
    ) {
      pages = await pdfFileToImages(file);
    } else {
      pages = await new Promise<string[]>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve([reader.result as string]);
        reader.readAsDataURL(file);
      });
    }
    const next: BenchmarkImage = {
      name: file.name,
      dataUrl: pages[0] ?? "",
      pages,
      updatedAt: new Date().toISOString(),
    };
    setStudentSubmission(next);
    setStudentPage(0);
    localStorage.setItem(STUDENT_SUBMISSION_KEY, JSON.stringify(next));
    setSubmissionResults(null);
    localStorage.removeItem(SUBMISSION_RESULTS_KEY);
  };

  const handleClearStudentSubmission = () => {
    setStudentSubmission(null);
    setStudentPage(0);
    localStorage.removeItem(STUDENT_SUBMISSION_KEY);
    setSubmissionResults(null);
    localStorage.removeItem(SUBMISSION_RESULTS_KEY);
  };

  const handleDownloadBenchmark = async () => {
    if (!benchmark) return;
    const res = await fetch(benchmark.dataUrl);
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = benchmark.name || "benchmark.jpg";
    a.rel = "noopener";
    a.click();
    URL.revokeObjectURL(url);
  };

  const runTask = async () => {
    setErrorMessage(null);

    if (!benchmark && activeTask !== "submissionGrader") {
      setErrorMessage("請先上傳 benchmark 圖片");
      return;
    }

    setIsRunning(true);
    setRunningStep(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const config = configs[activeTask];

      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      };

      if (activeTask === "questionIdentifier") {
        const pages = benchmark!.pages ?? [benchmark!.dataUrl];

        // Use the shared question identifier service (canonical implementation)
        const { questionResult: merged, extractedQuestions: extracted } = await processQuestionIdentifier(
          pages,
          config,
          (current, total, message) => setRunningStep(message)
        );

        persistResults({
          ...results,
          questionIdentifier: merged,
          extractedQuestions: extracted,
        });
        setRegionIndex(0);
      }

      if (activeTask === "rubricEditor") {
        if (!results.extractedQuestions?.length) {
          throw new Error("請先完成 Task 1（需偵測到至少一道題目與作答）");
        }

        // Use the shared rubric suggestion service (canonical implementation)
        const allRubrics = await suggestRubricsBatch(
          results.extractedQuestions,
          gradeLevel,
          config,
          (_batch, _total, message) => setRunningStep(message),
        );

        persistResults({ ...results, rubrics: allRubrics });
      }

      if (activeTask === "grader") {
        if (!results.extractedQuestions?.length) {
          throw new Error("請先完成 Task 1（需偵測到至少一道題目與作答）");
        }
        if (!results.rubrics?.length) {
          throw new Error("請先完成 Task 2（定義評分標準）");
        }

        // Batch processing to avoid token limits and rate limits
        const BATCH_SIZE = 5;
        const allGradedQuestions: any[] = [];
        const totalQuestions = results.extractedQuestions.length;
        const totalBatches = Math.ceil(totalQuestions / BATCH_SIZE);

        for (let batchIdx = 0; batchIdx < totalBatches; batchIdx++) {
          const start = batchIdx * BATCH_SIZE;
          const end = Math.min(start + BATCH_SIZE, totalQuestions);
          const batchQuestions = results.extractedQuestions.slice(start, end);

          // Get corresponding rubrics for this batch
          const batchQuestionIds = new Set(batchQuestions.map((q) => q.questionId));
          const batchRubrics = results.rubrics.filter((r) =>
            batchQuestionIds.has(r.questionId),
          );

          setRunningStep(
            `批改試卷 批次 ${batchIdx + 1}/${totalBatches}（題目 ${start + 1}-${end}/${totalQuestions}）`,
          );

          const response = await fetch("/api/dev/model-eval/grade-paper", {
            ...requestOptions,
            body: JSON.stringify({
              questions: batchQuestions,
              rubrics: batchRubrics,
              gradeLevel,
              modelConfig: config,
            }),
          });

          if (!response.ok) {
            throw new Error(`批次 ${batchIdx + 1} 失敗: ${await response.text()}`);
          }

          const batchData = await response.json();
          allGradedQuestions.push(...(batchData.questions || []));

          // Small delay between batches
          if (batchIdx < totalBatches - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }

        // Recalculate totals from all batches
        const totalMarks = allGradedQuestions.reduce(
          (sum, q) => sum + (q.marksAwarded ?? 0),
          0,
        );
        const maxTotalMarks = allGradedQuestions.reduce(
          (sum, q) => sum + (q.maxMarks ?? 0),
          0,
        );

        persistResults({
          ...results,
          grader: {
            questions: allGradedQuestions,
            totalMarks,
            maxTotalMarks,
            overallFeedback: `批改完成，共 ${allGradedQuestions.length} 題。`,
          },
        });
      }

      if (activeTask === "submissionGrader") {
        if (!studentSubmission) {
          throw new Error("請先上傳學生作答圖片");
        }
        if (!results.rubrics?.length) {
          throw new Error("請先完成 Task 2（定義評分標準）才能批改");
        }

        // Step 1: Run question identifier (OCR pass) on ALL pages of the student submission
        // Uses a separate model config so OCR and grading can use different models
        const subPages = studentSubmission.pages ?? [studentSubmission.dataUrl];
        const ocrConfig = configs["submissionGraderOcr"];

        // Use the shared question identifier service (canonical implementation)
        const { questionResult: mergedIdentifyData, extractedQuestions: extracted } = await processQuestionIdentifier(
          subPages,
          ocrConfig,
          (current, total, message) => setRunningStep(`${message}（步驟 1/2）`)
        );

        // Step 2: Grade using Task 2 rubrics (batched for reliability)
        const BATCH_SIZE = 5;
        const allGradedQuestions: any[] = [];
        const totalQuestions = extracted.length;
        const totalBatches = Math.ceil(totalQuestions / BATCH_SIZE);

        for (let batchIdx = 0; batchIdx < totalBatches; batchIdx++) {
          const start = batchIdx * BATCH_SIZE;
          const end = Math.min(start + BATCH_SIZE, totalQuestions);
          const batchQuestions = extracted.slice(start, end);

          const batchQuestionIds = new Set(batchQuestions.map((q) => q.questionId));
          const batchRubrics = results.rubrics.filter((r) =>
            batchQuestionIds.has(r.questionId),
          );

          setRunningStep(
            `AI 批改中 批次 ${batchIdx + 1}/${totalBatches}（題目 ${start + 1}-${end}/${totalQuestions}）（步驟 2/2）`,
          );

          const gradeResponse = await fetch("/api/dev/model-eval/grade-paper", {
            ...requestOptions,
            body: JSON.stringify({
              questions: batchQuestions,
              rubrics: batchRubrics,
              gradeLevel,
              modelConfig: config,
            }),
          });

          if (!gradeResponse.ok) {
            throw new Error(`批改批次 ${batchIdx + 1} 失敗: ${await gradeResponse.text()}`);
          }

          const batchData = await gradeResponse.json();
          allGradedQuestions.push(...(batchData.questions || []));

          if (batchIdx < totalBatches - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }

        const totalMarks = allGradedQuestions.reduce(
          (sum, q) => sum + (q.marksAwarded ?? 0),
          0,
        );
        const maxTotalMarks = allGradedQuestions.reduce(
          (sum, q) => sum + (q.maxMarks ?? 0),
          0,
        );

        const gradeData: GraderResult = {
          questions: allGradedQuestions,
          totalMarks,
          maxTotalMarks,
          overallFeedback: `批改完成，共 ${allGradedQuestions.length} 題。`,
        };

        const next = {
          questionIdentifier: mergedIdentifyData,
          extractedQuestions: extracted,
          grader: gradeData,
        };
        setSubmissionResults(next);
        localStorage.setItem(SUBMISSION_RESULTS_KEY, JSON.stringify(next));
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "執行失敗");
    } finally {
      setIsRunning(false);
      setRunningStep(null);
    }
  };

  const activeConfig = configs[activeTask];
  const activeSuggestions = suggestionsByTask[activeTask] || [];
  const regionOptions = results.questionIdentifier?.regions || [];
  const selectedRegion = regionOptions[regionIndex];

  // For Task 4: find the index within the current page's filtered regions for the hovered question
  const submissionHoverRegionIndex = useMemo(() => {
    if (!hoveredQuestionId || !submissionResults?.questionIdentifier?.regions)
      return undefined;
    const pageRegions = submissionResults.questionIdentifier.regions.filter(
      (r) => (r.pageIndex ?? 0) === studentPage,
    );
    const idx = pageRegions.findIndex(
      (r) =>
        r.parentQuestion === hoveredQuestionId &&
        (r.type ?? "").startsWith("answer-"),
    );
    return idx >= 0
      ? idx
      : pageRegions.findIndex((r) => r.questionId === hoveredQuestionId);
  }, [hoveredQuestionId, submissionResults, studentPage]);

  return (
    <Box>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          模型評估控制台 (Dev)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          上傳 benchmark 影像並依序執行各階段模型。結果與設定會保存在本機。
        </Typography>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            Benchmark 影像
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <Button variant="contained" component="label">
              上傳影像
              <input
                hidden
                type="file"
                accept="image/*,application/pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handleUpload(file);
                  event.target.value = "";
                }}
              />
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearBenchmark}
              disabled={!benchmark}
            >
              清除影像
            </Button>
            <Button
              variant="outlined"
              onClick={() => void handleDownloadBenchmark()}
              disabled={!benchmark}
            >
              下載影像
            </Button>
            {benchmark && (
              <Typography variant="body2" color="text.secondary">
                {benchmark.name}（
                {new Date(benchmark.updatedAt).toLocaleString()}）
                {(benchmark.pages?.length ?? 1) > 1 &&
                  ` · ${benchmark.pages.length} 頁`}
              </Typography>
            )}
          </Stack>
          {benchmark && (
            <>
              <Box
                component="img"
                src={benchmark.pages?.[benchmarkPage] ?? benchmark.dataUrl}
                alt="Benchmark"
                sx={{
                  maxWidth: 240,
                  maxHeight: 180,
                  objectFit: "contain",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              />
              <PageNavigator
                pages={benchmark.pages ?? [benchmark.dataUrl]}
                page={benchmarkPage}
                onPage={setBenchmarkPage}
              />
            </>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            學生作答（Task 4 用）
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <Button variant="contained" component="label" color="secondary">
              上傳學生作答
              <input
                hidden
                type="file"
                accept="image/*,application/pdf"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void handleUploadStudentSubmission(file);
                  event.target.value = "";
                }}
              />
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleClearStudentSubmission}
              disabled={!studentSubmission}
            >
              清除
            </Button>
            {studentSubmission && (
              <Typography variant="body2" color="text.secondary">
                {studentSubmission.name}（
                {new Date(studentSubmission.updatedAt).toLocaleString()}）
                {(studentSubmission.pages?.length ?? 1) > 1 &&
                  ` · ${studentSubmission.pages.length} 頁`}
              </Typography>
            )}
          </Stack>
          {studentSubmission && (
            <>
              <Box
                component="img"
                src={
                  studentSubmission.pages?.[studentPage] ??
                  studentSubmission.dataUrl
                }
                alt="Student submission"
                sx={{
                  maxWidth: 240,
                  maxHeight: 180,
                  objectFit: "contain",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "secondary.light",
                }}
              />
              <PageNavigator
                pages={studentSubmission.pages ?? [studentSubmission.dataUrl]}
                page={studentPage}
                onPage={setStudentPage}
              />
            </>
          )}
        </Stack>
      </Paper>

      {/* OCR model config — only shown for Task 4 (Submission Grader) */}
      {activeTask === "submissionGrader" && (() => {
        const ocrConfig = configs["submissionGraderOcr"];
        const ocrSuggestions = MODEL_SUGGESTIONS[ocrConfig.provider] || [];
        return (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                Pass 1 模型設定 — OCR 辨識（識別作答區域 + 轉錄手寫內容）
              </Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl sx={{ minWidth: 200 }}>
                  <InputLabel id="ocr-provider">Provider</InputLabel>
                  <Select
                    labelId="ocr-provider"
                    label="Provider"
                    value={ocrConfig.provider}
                    onChange={(event) =>
                      handleChange("submissionGraderOcr", {
                        provider: event.target.value,
                        model: MODEL_SUGGESTIONS[event.target.value]?.[0] || "",
                      })
                    }
                  >
                    {PROVIDERS.map((provider) => (
                      <MenuItem key={provider} value={provider}>
                        {provider}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Model"
                  value={ocrConfig.model}
                  onChange={(event) =>
                    handleChange("submissionGraderOcr", { model: event.target.value })
                  }
                  sx={{ flex: 1 }}
                />
              </Stack>
              {ocrSuggestions.length > 0 && (
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {ocrSuggestions.map((model) => (
                    <Chip
                      key={model}
                      label={model}
                      size="small"
                      onClick={() => handleChange("submissionGraderOcr", { model })}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              )}
              <TextField
                label="評估筆記"
                value={ocrConfig.notes}
                onChange={(event) =>
                  handleChange("submissionGraderOcr", { notes: event.target.value })
                }
                placeholder="紀錄此 OCR 模型的效果、辨識準確度等"
                multiline
                rows={2}
              />
            </Stack>
          </Paper>
        );
      })()}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            {activeTask === "submissionGrader"
              ? "Pass 2 模型設定 — 批改評分（對照評分標準）"
              : "模型設定"}
          </Typography>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="active-provider">Provider</InputLabel>
              <Select
                labelId="active-provider"
                label="Provider"
                value={activeConfig.provider}
                onChange={(event) =>
                  handleChange(activeTask, {
                    provider: event.target.value,
                    model: MODEL_SUGGESTIONS[event.target.value]?.[0] || "",
                  })
                }
              >
                {PROVIDERS.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Model"
              value={activeConfig.model}
              onChange={(event) =>
                handleChange(activeTask, { model: event.target.value })
              }
              sx={{ flex: 1 }}
            />
          </Stack>
          {activeSuggestions.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {activeSuggestions.map((model) => (
                <Chip
                  key={model}
                  label={model}
                  size="small"
                  onClick={() => handleChange(activeTask, { model })}
                  sx={{ mb: 1 }}
                />
              ))}
            </Stack>
          )}
          <TextField
            label="評估筆記"
            value={activeConfig.notes}
            onChange={(event) =>
              handleChange(activeTask, { notes: event.target.value })
            }
            placeholder="紀錄此模型在此階段的效果、失誤類型、優勢等"
            multiline
            rows={2}
          />
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
          >
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "儲存中…" : "儲存設定至伺服器"}
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              重設為預設值
            </Button>
            <Button variant="outlined" onClick={runTask} disabled={isRunning}>
              {isRunning ? "執行中…" : "執行此任務"}
            </Button>
          </Stack>
          {runningStep && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {runningStep}
              </Typography>
            </Stack>
          )}
          {serverSavedAt && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              💾 上次儲存：{new Date(serverSavedAt).toLocaleString()}（伺服器）—
              目前模型：
              <strong>
                {activeConfig.provider}/{activeConfig.model}
              </strong>
            </Typography>
          )}
          {saveMessage && (
            <Typography
              variant="body2"
              color={
                saveMessage.type === "success" ? "success.main" : "error.main"
              }
            >
              {saveMessage.type === "success" ? "✓" : "⚠"} {saveMessage.text}
            </Typography>
          )}
          {errorMessage && (
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          )}
        </Stack>
      </Paper>

      <Tabs
        value={activeTask}
        onChange={(_, value) => setActiveTask(value as TaskKey)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {TASKS.map((task) => (
          <Tab key={task.key} value={task.key} label={task.title} />
        ))}
      </Tabs>

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1" fontWeight={700}>
            {TASKS.find((task) => task.key === activeTask)?.description}
          </Typography>
          <Divider />

          {activeTask === "questionIdentifier" && (
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              sx={{ alignItems: "flex-start" }}
            >
              {/* 1:1:2 = 25%, 25%, 50% */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  輸入（Benchmark 影像）
                </Typography>
                {benchmark ? (
                  <>
                    <Box
                      component="img"
                      src={
                        benchmark.pages?.[benchmarkPage] ?? benchmark.dataUrl
                      }
                      alt="Benchmark"
                      sx={{
                        width: "100%",
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    <PageNavigator
                      pages={benchmark.pages ?? [benchmark.dataUrl]}
                      page={benchmarkPage}
                      onPage={setBenchmarkPage}
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    尚未上傳影像
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  輸出（偵測結果）
                </Typography>
                {results.questionIdentifier && benchmark ? (
                  <ImageWithRegions
                    src={benchmark.pages?.[benchmarkPage] ?? benchmark.dataUrl}
                    regions={results.questionIdentifier.regions.filter(
                      (r) => (r.pageIndex ?? 0) === benchmarkPage,
                    )}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    尚未執行 Task 1
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  flex: 2,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Raw JSON
                </Typography>
                {results.questionIdentifier ? (
                  <TextField
                    value={JSON.stringify(results.questionIdentifier, null, 2)}
                    multiline
                    fullWidth
                    InputProps={{
                      sx: {
                        fontFamily: "monospace",
                        fontSize: 12,
                        height: 600,
                        overflow: "auto",
                        alignItems: "flex-start",
                      },
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Box>
            </Stack>
          )}

          {activeTask === "rubricEditor" &&
            (() => {
              const updateRubric = (
                questionId: string,
                patch: Partial<Omit<QuestionRubric, "questionId">>,
              ) => {
                const current = results.rubrics || [];
                const idx = current.findIndex(
                  (r) => r.questionId === questionId,
                );
                if (idx === -1) {
                  const newRubric: QuestionRubric = {
                    questionId,
                    gradingMode: "auto",
                    maxMarks: 1,
                    ...patch,
                  };
                  persistResults({
                    ...results,
                    rubrics: [...current, newRubric],
                  });
                } else {
                  const updated = [...current];
                  updated[idx] = {
                    ...current[idx],
                    ...patch,
                    questionId,
                  } as QuestionRubric;
                  persistResults({ ...results, rubrics: updated });
                }
              };

              const updateCriterion = (
                questionId: string,
                criterionId: string,
                patch: Partial<RubricCriterion>,
              ) => {
                const rubric = (results.rubrics || []).find(
                  (r) => r.questionId === questionId,
                );
                if (!rubric?.criteria) return;
                const updatedCriteria = rubric.criteria.map((c) =>
                  c.id === criterionId ? { ...c, ...patch } : c,
                );
                updateRubric(questionId, {
                  criteria: updatedCriteria,
                  maxMarks: updatedCriteria.reduce(
                    (sum, c) => sum + c.points,
                    0,
                  ),
                });
              };

              const addCriterion = (questionId: string) => {
                const rubric = (results.rubrics || []).find(
                  (r) => r.questionId === questionId,
                );
                const criteria = rubric?.criteria || [];
                const newId = `c${criteria.length + 1}`;
                const updated = [
                  ...criteria,
                  { id: newId, description: "", points: 1 },
                ];
                updateRubric(questionId, {
                  criteria: updated,
                  maxMarks: updated.reduce((sum, c) => sum + c.points, 0),
                });
              };

              const removeCriterion = (
                questionId: string,
                criterionId: string,
              ) => {
                const rubric = (results.rubrics || []).find(
                  (r) => r.questionId === questionId,
                );
                if (!rubric?.criteria) return;
                const updated = rubric.criteria.filter(
                  (c) => c.id !== criterionId,
                );
                updateRubric(questionId, {
                  criteria: updated,
                  maxMarks: updated.reduce((sum, c) => sum + c.points, 0),
                });
              };

              const questions = results.extractedQuestions || [];
              const hasRubrics = (results.rubrics?.length || 0) > 0;
              const allRegions = results.questionIdentifier?.regions ?? [];
              const benchmarkPages = benchmark?.pages ?? (benchmark ? [benchmark.dataUrl] : []);
              const pageRegions = allRegions.filter((r) => (r.pageIndex ?? 0) === rubricPageIndex);
              const highlightIdx = focusedQuestionId != null
                ? pageRegions.findIndex((r) => r.questionId === focusedQuestionId)
                : undefined;

              return (
                <Stack spacing={2}>
                  {/* Header row */}
                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <TextField
                      label="年級"
                      value={gradeLevel}
                      onChange={(event) => setGradeLevel(event.target.value)}
                      size="small"
                      sx={{ width: 120 }}
                    />
                    <Button
                      variant="outlined"
                      disabled={isRunning || questions.length === 0}
                      onClick={async () => {
                        setIsRunning(true);
                        setRunningStep(null);
                        try {
                          const allRubrics = await suggestRubricsBatch(
                            questions,
                            gradeLevel,
                            configs.rubricEditor,
                            (_b, _t, msg) => setRunningStep(msg),
                          );
                          persistResults({ ...results, rubrics: allRubrics });
                        } catch (err) {
                          setErrorMessage(err instanceof Error ? err.message : "生成評分標準失敗");
                        } finally {
                          setIsRunning(false);
                          setRunningStep(null);
                        }
                      }}
                    >
                      {isRunning ? <CircularProgress size={20} /> : "AI 自動建議"}
                    </Button>
                    {hasRubrics && (
                      <Button
                        size="small"
                        variant="text"
                        color="warning"
                        onClick={() =>
                          persistResults({ ...results, rubrics: undefined, grader: undefined })
                        }
                      >
                        清除全部
                      </Button>
                    )}
                    {hasRubrics && (
                      <Typography variant="caption" color="text.secondary">
                        共 {results.rubrics!.length} 題 · 總分{" "}
                        {results.rubrics!.reduce((s, r) => s + r.maxMarks, 0)}
                      </Typography>
                    )}
                  </Stack>

                  {runningStep && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">{runningStep}</Typography>
                    </Stack>
                  )}

                  {questions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      尚未完成 Task 1（或未偵測到題目）。請先執行 Task 1。
                    </Typography>
                  ) : (
                    <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ alignItems: "flex-start" }}>
                      {/* Left: benchmark image with region overlay (sticky) */}
                      <Box sx={{ flex: "none", width: { xs: "100%", lg: 420 }, position: "sticky", top: 16 }}>
                        {benchmarkPages.length > 1 && (
                          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <IconButton
                              size="small"
                              disabled={rubricPageIndex <= 0}
                              onClick={() => setRubricPageIndex((p) => p - 1)}
                            >
                              <ArrowBackIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="body2" color="text.secondary">
                              第 {rubricPageIndex + 1} / {benchmarkPages.length} 頁
                            </Typography>
                            <IconButton
                              size="small"
                              disabled={rubricPageIndex >= benchmarkPages.length - 1}
                              onClick={() => setRubricPageIndex((p) => p + 1)}
                            >
                              <ArrowForwardIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        )}
                        {benchmarkPages[rubricPageIndex] ? (
                          <SharedImageWithRegions
                            src={benchmarkPages[rubricPageIndex]!}
                            regions={pageRegions}
                            highlightIndex={highlightIdx !== undefined && highlightIdx >= 0 ? highlightIdx : undefined}
                            onRegionHoverEnter={(questionId) => setFocusedQuestionId(questionId)}
                            onRegionHoverLeave={() => setFocusedQuestionId(null)}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            尚未上傳 Benchmark 影像
                          </Typography>
                        )}
                      </Box>

                      {/* Right: rubric editor list */}
                      <Box sx={{ flex: 1, minWidth: 0, maxHeight: 700, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                        {questions.map((q, i) => {
                          const rubric = (results.rubrics || []).find(
                            (r) => r.questionId === q.questionId,
                          );
                          const mode = rubric?.gradingMode || "auto";
                          const isFocused = focusedQuestionId === q.questionId;
                          const isSuggesting = suggestingQuestion === q.questionId;
                          const boxAccent = getQuestionRubricAccentColor(allRegions, q.questionId);
                          const strokeColor = isFocused ? HIGHLIGHT_STROKE : boxAccent;
                          const rowBg = isFocused
                            ? alpha(HIGHLIGHT_STROKE, 0.14)
                            : boxAccent != null
                              ? alpha(boxAccent, 0.09)
                              : i % 2 === 0 ? "background.paper" : "grey.50";

                          return (
                            <Box
                              key={q.questionId}
                              onMouseEnter={() => {
                                setFocusedQuestionId(q.questionId);
                                const region =
                                  allRegions.find(
                                    (r) => r.questionId === q.questionId && (r.type === "question" || r.type === "mc-question"),
                                  ) ||
                                  allRegions.find(
                                    (r) => r.parentQuestion === q.questionId && (r.type?.startsWith("answer-") ?? false),
                                  );
                                if (region && (region.pageIndex ?? 0) !== rubricPageIndex) {
                                  setRubricPageIndex(region.pageIndex ?? 0);
                                }
                              }}
                              onMouseLeave={() => setFocusedQuestionId(null)}
                              sx={{
                                p: 2,
                                pl: 1.25,
                                borderBottom: i < questions.length - 1 ? "1px solid" : "none",
                                borderColor: "divider",
                                borderLeft: "4px solid",
                                borderLeftColor: strokeColor ?? "transparent",
                                bgcolor: rowBg,
                                transition: "background-color 0.15s, border-color 0.15s, outline 0.15s",
                                outline: isFocused ? `2px solid ${HIGHLIGHT_STROKE}` : "none",
                                outlineOffset: "-2px",
                              }}
                            >
                              {/* Question header: ID + type chip (left) · score field (right) */}
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ mb: 0.5 }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    sx={{ color: isFocused ? HIGHLIGHT_STROKE : boxAccent ?? "primary.main" }}
                                  >
                                    {q.questionId}
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={q.questionType}
                                    variant="outlined"
                                    sx={
                                      boxAccent || isFocused
                                        ? {
                                            borderColor: isFocused ? HIGHLIGHT_STROKE : boxAccent!,
                                            color: isFocused ? HIGHLIGHT_STROKE : boxAccent!,
                                          }
                                        : undefined
                                    }
                                  />
                                </Stack>
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="caption" color="text.secondary">分數</Typography>
                                  <TextField
                                    type="number"
                                    value={rubric?.maxMarks ?? 1}
                                    onChange={(e) =>
                                      updateRubric(q.questionId, { maxMarks: Math.max(0, Number(e.target.value)) })
                                    }
                                    size="small"
                                    sx={{ width: 65 }}
                                    inputProps={{ min: 0, step: 0.5 }}
                                  />
                                </Stack>
                              </Stack>

                              {q.sectionInstruction && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                  {q.sectionInstruction}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ mb: 1 }}>{q.questionText}</Typography>

                              {/* Per-question AI suggest + mode selector row */}
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={isSuggesting}
                                  onClick={async () => {
                                    setSuggestingQuestion(q.questionId);
                                    try {
                                      const suggested = await suggestSingleRubric(q, gradeLevel, configs.rubricEditor);
                                      if (suggested) {
                                        updateRubric(q.questionId, {
                                          gradingMode: suggested.gradingMode,
                                          maxMarks: suggested.maxMarks,
                                          modelAnswer: suggested.modelAnswer,
                                          criteria: suggested.criteria,
                                          notes: suggested.notes,
                                        });
                                      }
                                    } catch (err) {
                                      setErrorMessage(err instanceof Error ? err.message : "生成評分標準失敗");
                                    } finally {
                                      setSuggestingQuestion(null);
                                    }
                                  }}
                                >
                                  {isSuggesting ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
                                  AI 建議
                                </Button>
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                  <InputLabel>評分模式</InputLabel>
                                  <Select
                                    label="評分模式"
                                    value={mode}
                                    onChange={(e) => {
                                      const newMode = e.target.value as "auto" | "rubric";
                                      if (newMode === "rubric" && !rubric?.criteria?.length) {
                                        updateRubric(q.questionId, {
                                          gradingMode: "rubric",
                                          criteria: [{ id: "c1", description: "", points: 1 }],
                                          maxMarks: rubric?.maxMarks ?? 1,
                                        });
                                      } else {
                                        updateRubric(q.questionId, { gradingMode: newMode });
                                      }
                                    }}
                                  >
                                    <MenuItem value="auto">自動（對/錯）</MenuItem>
                                    <MenuItem value="rubric">評分標準（多項）</MenuItem>
                                  </Select>
                                </FormControl>
                              </Stack>

                              {/* Model answer (full width) */}
                              <TextField
                                label="參考答案"
                                value={rubric?.modelAnswer || ""}
                                onChange={(e) => updateRubric(q.questionId, { modelAnswer: e.target.value })}
                                size="small"
                                fullWidth
                                sx={{ mb: 1 }}
                              />

                              {/* Criteria list (rubric mode only) */}
                              {mode === "rubric" && rubric && (
                                <Box
                                  sx={{
                                    pl: 1,
                                    borderLeft: "2px solid",
                                    borderColor: boxAccent ?? "secondary.light",
                                  }}
                                >
                                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                                    評分標準（{rubric.criteria?.length || 0} 項，共 {rubric.maxMarks} 分）
                                  </Typography>
                                  <Stack spacing={0.75} sx={{ mb: 1 }}>
                                    {(rubric.criteria || []).map((c, ci) => (
                                      <Stack key={c.id} direction="row" spacing={1} alignItems="center">
                                        <Typography
                                          variant="caption"
                                          sx={{ width: 20, textAlign: "center", color: "text.secondary", flexShrink: 0 }}
                                        >
                                          {ci + 1}
                                        </Typography>
                                        <TextField
                                          value={c.description}
                                          onChange={(e) =>
                                            updateCriterion(q.questionId, c.id, { description: e.target.value })
                                          }
                                          placeholder="評分標準描述"
                                          size="small"
                                          sx={{ flex: 1 }}
                                        />
                                        <TextField
                                          type="number"
                                          value={c.points}
                                          onChange={(e) =>
                                            updateCriterion(q.questionId, c.id, { points: Math.max(0, Number(e.target.value)) })
                                          }
                                          size="small"
                                          sx={{ width: 65 }}
                                          inputProps={{ min: 0, step: 0.5 }}
                                          label="分"
                                        />
                                        <IconButton
                                          size="small"
                                          onClick={() => removeCriterion(q.questionId, c.id)}
                                          color="error"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Stack>
                                    ))}
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => addCriterion(q.questionId)}
                                      sx={{ alignSelf: "flex-start" }}
                                    >
                                      新增標準
                                    </Button>
                                  </Stack>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Stack>
                  )}
                </Stack>
              );
            })()}

          {activeTask === "grader" && (
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={2}
              sx={{ alignItems: "flex-start" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  輸入（題目與作答）
                </Typography>
                {results.extractedQuestions &&
                results.extractedQuestions.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: 500,
                      overflow: "auto",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    {results.extractedQuestions.map((q) => (
                      <Box
                        key={q.questionId}
                        sx={{
                          mb: 1,
                          pb: 1,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="primary.main"
                        >
                          {q.questionId} [{q.questionType}]
                        </Typography>
                        <Typography variant="body2">
                          {q.questionText}
                        </Typography>
                        <Chip
                          size="small"
                          label={`作答：${q.studentAnswer || "（空白）"}`}
                          sx={{ mt: 0.5 }}
                          color={q.studentAnswer ? "default" : "warning"}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    尚未完成 Task 1
                  </Typography>
                )}
                {!results.rubrics?.length && (
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ mt: 1 }}
                  >
                    請先完成 Task 2（定義評分標準）才能執行評分。
                  </Typography>
                )}
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" gutterBottom>
                  評分結果
                </Typography>
                {results.grader ? (
                  <>
                    <Box
                      sx={{
                        mb: 2,
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "grey.50",
                      }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        總分：{results.grader.totalMarks} /{" "}
                        {results.grader.maxTotalMarks}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {results.grader.overallFeedback}
                      </Typography>
                    </Box>
                    <Box sx={{ maxHeight: 500, overflow: "auto" }}>
                      {results.grader.questions?.map((q) => (
                        <Box
                          key={q.questionId}
                          sx={{
                            mb: 1.5,
                            p: 1,
                            border: "1px solid",
                            borderColor:
                              q.correct === false
                                ? "error.light"
                                : q.correct
                                  ? "success.light"
                                  : "divider",
                            borderRadius: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography variant="caption" fontWeight={700}>
                              {q.questionId}
                            </Typography>
                            <Chip
                              size="small"
                              label={
                                q.correct === true
                                  ? "正確"
                                  : q.correct === false
                                    ? "錯誤"
                                    : "部分正確"
                              }
                              color={
                                q.correct === true
                                  ? "success"
                                  : q.correct === false
                                    ? "error"
                                    : "warning"
                              }
                              variant="outlined"
                            />
                            <Typography variant="caption">
                              {q.marksAwarded}/{q.maxMarks} 分
                            </Typography>
                          </Stack>
                          {q.modelAnswer && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              參考答案：{q.modelAnswer}
                            </Typography>
                          )}
                          {q.criteriaResults &&
                            q.criteriaResults.length > 0 && (
                              <Box sx={{ mt: 0.5, pl: 1 }}>
                                {q.criteriaResults.map((cr) => (
                                  <Typography
                                    key={cr.criterionId}
                                    variant="caption"
                                    display="block"
                                    sx={{
                                      color:
                                        cr.awarded >= cr.maxPoints
                                          ? "success.main"
                                          : cr.awarded > 0
                                            ? "warning.main"
                                            : "error.main",
                                    }}
                                  >
                                    [{cr.criterionId}] {cr.awarded}/
                                    {cr.maxPoints} — {cr.comment}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {q.feedback}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box
                      component="pre"
                      sx={{
                        mt: 2,
                        fontFamily: "monospace",
                        fontSize: 11,
                        maxHeight: 250,
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        p: 1,
                        m: 0,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "grey.50",
                      }}
                    >
                      {JSON.stringify(results.grader, null, 2)}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    尚未執行 Task 3
                  </Typography>
                )}
              </Box>
            </Stack>
          )}

          {activeTask === "submissionGrader" && (
            <Stack spacing={2}>
              {/* Prerequisites check */}
              {!results.rubrics?.length && (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "warning.50",
                    border: "1px solid",
                    borderColor: "warning.light",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" color="warning.dark">
                    ⚠ 請先完成 Task 1（偵測題目結構）及 Task
                    2（定義評分標準），再執行 Task 4。
                  </Typography>
                </Box>
              )}

              {/* Running progress */}
              {isRunning && activeTask === "submissionGrader" && (
                <Box
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor: "primary.light",
                    borderRadius: 1,
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2" fontWeight={600}>
                        {runningStep ?? "處理中…"}
                      </Typography>
                    </Stack>
                    <LinearProgress />
                  </Stack>
                </Box>
              )}

              {/* Main visual area once we have results */}
              {submissionResults && studentSubmission ? (
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={2}
                  sx={{ alignItems: "flex-start" }}
                >
                  {/* LEFT: student submission with detected regions overlaid */}
                  <Box
                    sx={{ flex: 1, minWidth: 0, position: "sticky", top: 16 }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        學生作答（偵測結果）
                      </Typography>
                      {submissionResults.questionIdentifier?.regions && (
                        <Chip
                          size="small"
                          label={`${submissionResults.questionIdentifier.regions.length} 個區域`}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    {submissionResults.questionIdentifier && (
                      <ImageWithRegions
                        src={
                          studentSubmission.pages?.[studentPage] ??
                          studentSubmission.dataUrl
                        }
                        regions={submissionResults.questionIdentifier.regions.filter(
                          (r) => (r.pageIndex ?? 0) === studentPage,
                        )}
                        highlightIndex={submissionHoverRegionIndex}
                        showCoordinates={false}
                      />
                    )}
                    {hoveredQuestionId && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        高亮顯示：{hoveredQuestionId} 的作答區域
                      </Typography>
                    )}
                    <PageNavigator
                      pages={
                        studentSubmission.pages ?? [studentSubmission.dataUrl]
                      }
                      page={studentPage}
                      onPage={setStudentPage}
                    />
                  </Box>

                  {/* RIGHT: grading results */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Total score banner */}
                    {submissionResults.grader && (
                      <Paper
                        sx={{
                          p: 2,
                          mb: 2,
                          textAlign: "center",
                          bgcolor:
                            submissionResults.grader.totalMarks /
                              submissionResults.grader.maxTotalMarks >=
                            0.7
                              ? "success.50"
                              : submissionResults.grader.totalMarks /
                                    submissionResults.grader.maxTotalMarks >=
                                  0.4
                                ? "warning.50"
                                : "error.50",
                          border: "1px solid",
                          borderColor:
                            submissionResults.grader.totalMarks /
                              submissionResults.grader.maxTotalMarks >=
                            0.7
                              ? "success.light"
                              : submissionResults.grader.totalMarks /
                                    submissionResults.grader.maxTotalMarks >=
                                  0.4
                                ? "warning.light"
                                : "error.light",
                        }}
                      >
                        <Typography
                          variant="h3"
                          fontWeight={700}
                          color="text.primary"
                        >
                          {submissionResults.grader.totalMarks}
                          <Typography
                            component="span"
                            variant="h5"
                            color="text.secondary"
                          >
                            {" "}
                            / {submissionResults.grader.maxTotalMarks}
                          </Typography>
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {submissionResults.grader.overallFeedback}
                        </Typography>
                      </Paper>
                    )}

                    {/* Per-question results */}
                    <Stack
                      spacing={1}
                      sx={{ maxHeight: 700, overflow: "auto", pr: 0.5 }}
                    >
                      {submissionResults.grader?.questions.map((q) => {
                        const eq = submissionResults.extractedQuestions?.find(
                          (x) => x.questionId === q.questionId,
                        );
                        const pct =
                          q.maxMarks > 0 ? q.marksAwarded / q.maxMarks : 0;
                        const scoreColor =
                          pct >= 1 ? "success" : pct > 0 ? "warning" : "error";
                        const isHovered = hoveredQuestionId === q.questionId;

                        return (
                          <Paper
                            key={q.questionId}
                            variant="outlined"
                            onMouseEnter={() =>
                              setHoveredQuestionId(q.questionId)
                            }
                            onMouseLeave={() => setHoveredQuestionId(null)}
                            sx={{
                              p: 1.5,
                              cursor: "default",
                              transition:
                                "box-shadow 0.15s, border-color 0.15s",
                              borderColor: isHovered
                                ? `${scoreColor}.main`
                                : "divider",
                              boxShadow: isHovered ? 2 : 0,
                            }}
                          >
                            {/* Question header row */}
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                              sx={{ mb: 0.75 }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                sx={{ minWidth: 40 }}
                              >
                                {q.questionId}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${q.marksAwarded} / ${q.maxMarks} 分`}
                                color={scoreColor}
                                variant={pct >= 1 ? "filled" : "outlined"}
                              />
                              {q.correct !== null &&
                                q.correct !== undefined && (
                                  <Chip
                                    size="small"
                                    label={q.correct ? "✓ 正確" : "✗ 錯誤"}
                                    color={q.correct ? "success" : "error"}
                                    variant="outlined"
                                  />
                                )}
                              <Tooltip
                                title={`信心度 ${(q.confidence * 100).toFixed(0)}%`}
                              >
                                <Chip
                                  size="small"
                                  label={`信心 ${(q.confidence * 100).toFixed(0)}%`}
                                  color={
                                    q.confidence >= 0.7 ? "default" : "warning"
                                  }
                                  variant="outlined"
                                  sx={{ ml: "auto" }}
                                />
                              </Tooltip>
                            </Stack>

                            {/* Transcribed student answer */}
                            {eq?.studentAnswer && (
                              <Box
                                sx={{
                                  mb: 0.75,
                                  px: 1,
                                  py: 0.5,
                                  bgcolor: "grey.100",
                                  borderRadius: 1,
                                  borderLeft: "3px solid",
                                  borderColor: "grey.400",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mb: 0.25 }}
                                >
                                  學生作答（AI 辨識）
                                </Typography>
                                <Typography
                                  variant="body2"
                                  fontFamily="monospace"
                                >
                                  {eq.studentAnswer || "（未辨識到作答）"}
                                </Typography>
                              </Box>
                            )}

                            {/* Model answer */}
                            {q.modelAnswer && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                sx={{ mb: 0.5 }}
                              >
                                參考答案：{q.modelAnswer}
                              </Typography>
                            )}

                            {/* Per-criterion breakdown */}
                            {q.criteriaResults &&
                              q.criteriaResults.length > 0 && (
                                <Stack spacing={0.25} sx={{ mb: 0.75, pl: 1 }}>
                                  {q.criteriaResults.map((cr) => (
                                    <Typography
                                      key={cr.criterionId}
                                      variant="caption"
                                      display="block"
                                      sx={{
                                        color:
                                          cr.awarded >= cr.maxPoints
                                            ? "success.main"
                                            : cr.awarded > 0
                                              ? "warning.main"
                                              : "error.main",
                                      }}
                                    >
                                      [{cr.criterionId}] {cr.awarded}/
                                      {cr.maxPoints} 分 — {cr.comment}
                                    </Typography>
                                  ))}
                                </Stack>
                              )}

                            {/* Feedback */}
                            <Typography variant="body2" color="text.secondary">
                              {q.feedback}
                            </Typography>
                          </Paper>
                        );
                      })}
                    </Stack>

                    {/* Clear results button */}
                    <Button
                      size="small"
                      variant="outlined"
                      color="warning"
                      sx={{ mt: 1.5 }}
                      onClick={() => {
                        setSubmissionResults(null);
                        localStorage.removeItem(SUBMISSION_RESULTS_KEY);
                      }}
                    >
                      清除批改結果
                    </Button>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  {!studentSubmission ? (
                    <Typography variant="body2" color="text.secondary">
                      請在上方上傳學生作答圖片，然後按「執行此任務」
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      按「執行此任務」開始偵測並批改學生作答
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
